/**
 * 로컬 알림 관리 모듈
 *
 * 기기 자체에서 발송하는 알림(서버 불필요)을 시나리오별로 관리합니다.
 *
 * 시나리오:
 *   A. 일일 기록 리마인더 — 매일 설정 시간 반복
 *   B. 기억 되살리기     — 내일 14시 1회 (앱 미실행 시에만 도착)
 *   C. 주간 회고         — 매주 일요일 10시 반복
 *   D. 스트릭 축하/위기  — 즉시 또는 리마인더+1시간
 *   E. 이탈 방지         — 3일/7일/14일 후 20시 1회
 *
 * 웹 환경에서 실행되면 아무 동작 없이 안전하게 무시됩니다.
 */

import { Capacitor } from '@capacitor/core';
import { LocalNotifications } from '@capacitor/local-notifications';

// ── 알림 ID 상수 (같은 ID로 예약하면 이전 알림을 덮어씀) ────────
const JOURNAL_REMINDER_ID    = 1001; // A. 일일 리마인더
const MEMORY_RESURFACING_ID  = 1002; // B. 기억 되살리기
const WEEKLY_REVIEW_ID       = 1003; // C. 주간 회고
const STREAK_CELEBRATE_ID    = 1004; // D-1. 스트릭 축하
const STREAK_DANGER_ID       = 1005; // D-2. 스트릭 위기
const REENGAGE_3DAY_ID       = 1006; // E-1. 3일 미사용
const REENGAGE_7DAY_ID       = 1007; // E-2. 7일 미사용
const REENGAGE_14DAY_ID      = 1008; // E-3. 14일 미사용
const DECISION_FOLLOWUP_ID   = 1009; // F. 고민 상담 후속
const FIRST_RECORD_ID        = 1010; // H. 첫 기록 기념
const NEW_CHAT_MSG_ID        = 1011; // I. 나와의 대화 미확인
const DREAM_FIRSTSTEP_ID     = 1012; // G. 꿈과 목표 첫 행동 팔로우업

// ── 공통 알림 옵션 ──────────────────────────────────────────────
const NOTIF_DEFAULTS = {
  sound: undefined,  // 기기 기본 알림음 사용
  smallIcon: 'ic_stat_icon_config_sample',
};

// ===================================================================
// 권한 요청
// ===================================================================

/**
 * 알림 권한을 사용자에게 요청합니다.
 * @returns {Promise<boolean>} 권한이 허용되면 true, 거부되면 false
 */
export async function requestNotificationPermission() {
  if (!Capacitor.isNativePlatform()) return false;

  try {
    const { display } = await LocalNotifications.requestPermissions();
    return display === 'granted';
  } catch (err) {
    console.warn('[알림] 권한 요청 실패:', err);
    return false;
  }
}

// ===================================================================
// A. 일일 기록 리마인더
// ===================================================================

/**
 * 매일 정해진 시간에 기록 리마인더를 예약합니다.
 * @param {number} hour - 알림 시(0~23), 기본 21
 * @param {number} minute - 알림 분(0~59), 기본 0
 */
export async function scheduleJournalReminder(hour = 21, minute = 0) {
  if (!Capacitor.isNativePlatform()) return;

  try {
    await cancelJournalReminder();
    await LocalNotifications.schedule({
      notifications: [{
        ...NOTIF_DEFAULTS,
        id: JOURNAL_REMINDER_ID,
        title: '오늘 하루는 어땠나요?',
        body: '잠들기 전, 한 줄이라도 남겨보세요.',
        extra: { type: 'daily-reminder' },
        // iOS에서 꾹 누르면 답장 버튼이 표시되는 카테고리 (AppDelegate.swift에 등록됨)
        categoryIdentifier: 'DAILY_CHECKIN',
        schedule: {
          on: { hour, minute },
          repeats: true,
          allowWhileIdle: true,
        },
      }],
    });
    console.log(`[알림] A. 매일 ${hour}:${String(minute).padStart(2, '0')} 리마인더 예약`);
  } catch (err) {
    console.warn('[알림] A. 리마인더 예약 실패:', err);
  }
}

/** 일일 리마인더 취소 */
export async function cancelJournalReminder() {
  if (!Capacitor.isNativePlatform()) return;
  try {
    await LocalNotifications.cancel({ notifications: [{ id: JOURNAL_REMINDER_ID }] });
  } catch (err) {
    console.warn('[알림] A. 리마인더 취소 실패:', err);
  }
}

// ===================================================================
// B. 기억 되살리기 푸시
// ===================================================================

/**
 * 내일 14시에 기억 되살리기 알림을 1회 예약합니다.
 * 앱을 열면 인앱 채팅이 처리하므로, 앱을 안 열었을 때만 도착합니다.
 *
 * @param {number} daysAgo - 대상 기록이 며칠 전인지 (제목에 표시)
 */
export async function scheduleMemoryResurfacingPush(daysAgo) {
  if (!Capacitor.isNativePlatform()) return;

  try {
    await cancelMemoryResurfacingPush();

    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(14, 0, 0, 0);

    await LocalNotifications.schedule({
      notifications: [{
        ...NOTIF_DEFAULTS,
        id: MEMORY_RESURFACING_ID,
        title: `${daysAgo}일 전, 이런 기록을 남겼어요`,
        body: '그때의 이야기를 조금 더 나눠볼까요?',
        extra: { type: 'memory-resurfacing' },
        schedule: { at: tomorrow, allowWhileIdle: true },
      }],
    });
    console.log(`[알림] B. 내일 14시 기억 되살리기 예약 (${daysAgo}일 전 기록)`);
  } catch (err) {
    console.warn('[알림] B. 기억 되살리기 예약 실패:', err);
  }
}

/** 기억 되살리기 푸시 취소 */
export async function cancelMemoryResurfacingPush() {
  if (!Capacitor.isNativePlatform()) return;
  try {
    await LocalNotifications.cancel({ notifications: [{ id: MEMORY_RESURFACING_ID }] });
  } catch (err) {
    console.warn('[알림] B. 기억 되살리기 취소 실패:', err);
  }
}

// ===================================================================
// C. 주간 회고 푸시
// ===================================================================

/**
 * 매주 일요일 10시에 주간 회고 알림을 반복 예약합니다.
 * 앱을 열면 인앱 채팅이 회고를 생성하므로, 앱을 안 열었을 때만 의미 있습니다.
 */
export async function scheduleWeeklyReviewPush() {
  if (!Capacitor.isNativePlatform()) return;

  try {
    await cancelWeeklyReviewPush();
    await LocalNotifications.schedule({
      notifications: [{
        ...NOTIF_DEFAULTS,
        id: WEEKLY_REVIEW_ID,
        title: '이번 주 기록을 돌아봤어요',
        body: '한 주간의 흐름을 정리해뒀어요. 확인해볼까요?',
        extra: { type: 'weekly-review' },
        schedule: {
          on: { weekday: 1, hour: 10, minute: 0 }, // weekday 1 = 일요일
          repeats: true,
          allowWhileIdle: true,
        },
      }],
    });
    console.log('[알림] C. 매주 일요일 10시 주간 회고 예약');
  } catch (err) {
    console.warn('[알림] C. 주간 회고 예약 실패:', err);
  }
}

/** 주간 회고 푸시 취소 */
export async function cancelWeeklyReviewPush() {
  if (!Capacitor.isNativePlatform()) return;
  try {
    await LocalNotifications.cancel({ notifications: [{ id: WEEKLY_REVIEW_ID }] });
  } catch (err) {
    console.warn('[알림] C. 주간 회고 취소 실패:', err);
  }
}

// ===================================================================
// D-1. 스트릭 축하 (즉시 발송)
// ===================================================================

// 마일스톤별 축하 메시지
const STREAK_MESSAGES = {
  3:   { title: '3일 연속 기록 달성!',      body: '좋은 습관이 만들어지고 있어요.' },
  7:   { title: '일주일 내내 기록했어요!',    body: '꾸준한 일주일, 정말 대단해요.' },
  14:  { title: '2주 연속 기록 중!',         body: '기록이 습관이 되어가고 있네요.' },
  30:  { title: '한 달 연속 기록 달성!',      body: '30일 동안의 기록이 쌓였어요. 대단해요.' },
  50:  { title: '50일째 이어지는 기록',       body: '꾸준함이 만들어낸 소중한 기록이에요.' },
  100: { title: '100일 연속 기록!',          body: '정말 대단해요. 이 기록들이 큰 자산이 될 거예요.' },
};

/**
 * 스트릭 마일스톤 달성 시 즉시 축하 알림을 보냅니다.
 * @param {number} streakDays - 연속 기록 일수 (3, 7, 14, 30, 50, 100)
 */
export async function sendStreakCelebration(streakDays) {
  if (!Capacitor.isNativePlatform()) return;

  const msg = STREAK_MESSAGES[streakDays];
  if (!msg) return; // 마일스톤이 아니면 무시

  try {
    await LocalNotifications.schedule({
      notifications: [{
        ...NOTIF_DEFAULTS,
        id: STREAK_CELEBRATE_ID,
        title: msg.title,
        body: msg.body,
        extra: { type: 'streak-celebrate' },
        schedule: { at: new Date(Date.now() + 3000) }, // 3초 후 발송
      }],
    });
    console.log(`[알림] D-1. 스트릭 ${streakDays}일 축하 발송`);
  } catch (err) {
    console.warn('[알림] D-1. 스트릭 축하 실패:', err);
  }
}

// ===================================================================
// D-2. 스트릭 위기 (오늘 기록 안 했을 때, 리마인더+1시간 후)
// ===================================================================

/**
 * 스트릭이 위험할 때 (오늘 기록 안 했을 때) 리마인더 1시간 후 알림을 예약합니다.
 * @param {number} streakDays - 현재 이어지고 있는 스트릭 일수
 * @param {number} reminderHour - 사용자가 설정한 리마인더 시간 (기본 21)
 */
export async function scheduleStreakDangerPush(streakDays, reminderHour = 21) {
  if (!Capacitor.isNativePlatform()) return;

  try {
    await cancelStreakDangerPush();

    const targetTime = new Date();
    targetTime.setHours(reminderHour + 1, 0, 0, 0);

    // 이미 지난 시간이면 예약하지 않음
    if (targetTime <= new Date()) return;

    await LocalNotifications.schedule({
      notifications: [{
        ...NOTIF_DEFAULTS,
        id: STREAK_DANGER_ID,
        title: `${streakDays}일째 이어지고 있는 기록`,
        body: '오늘도 한 줄 남겨볼까요?',
        extra: { type: 'streak-danger' },
        schedule: { at: targetTime, allowWhileIdle: true },
      }],
    });
    console.log(`[알림] D-2. 스트릭 위기 알림 ${reminderHour + 1}시 예약 (${streakDays}일 스트릭)`);
  } catch (err) {
    console.warn('[알림] D-2. 스트릭 위기 예약 실패:', err);
  }
}

/** 스트릭 위기 알림 취소 */
export async function cancelStreakDangerPush() {
  if (!Capacitor.isNativePlatform()) return;
  try {
    await LocalNotifications.cancel({ notifications: [{ id: STREAK_DANGER_ID }] });
  } catch (err) {
    console.warn('[알림] D-2. 스트릭 위기 취소 실패:', err);
  }
}

// ===================================================================
// E. 이탈 방지 (재참여)
// ===================================================================

/**
 * 앱 열릴 때마다 호출: 기존 이탈 방지 알림을 취소하고 3/7/14일 후로 재예약합니다.
 * 앱을 열 때마다 타이머가 리셋되므로, 앱을 사용하는 동안에는 도착하지 않습니다.
 */
export async function scheduleReengagementPushes() {
  if (!Capacitor.isNativePlatform()) return;

  try {
    await cancelReengagementPushes();

    const items = [
      { id: REENGAGE_3DAY_ID,  days: 3,  title: '요즘 어떻게 지내고 있나요?', body: '한 줄이라도 남겨보는 건 어때요.' },
      { id: REENGAGE_7DAY_ID,  days: 7,  title: '일주일 동안의 이야기',       body: '기록해두면 나중에 소중한 기억이 될 거예요.' },
      { id: REENGAGE_14DAY_ID, days: 14, title: '돌아와주면 좋겠어요',        body: '언제든 기다리고 있을게요.' },
    ];

    const notifications = items.map(({ id, days, title, body }) => {
      const at = new Date();
      at.setDate(at.getDate() + days);
      at.setHours(20, 0, 0, 0); // 오후 8시에 발송
      return { ...NOTIF_DEFAULTS, id, title, body, extra: { type: 're-engagement' }, schedule: { at, allowWhileIdle: true } };
    });

    await LocalNotifications.schedule({ notifications });
    console.log('[알림] E. 이탈 방지 알림 3/7/14일 후 예약');
  } catch (err) {
    console.warn('[알림] E. 이탈 방지 예약 실패:', err);
  }
}

/** 이탈 방지 알림 전체 취소 */
export async function cancelReengagementPushes() {
  if (!Capacitor.isNativePlatform()) return;
  try {
    await LocalNotifications.cancel({
      notifications: [
        { id: REENGAGE_3DAY_ID },
        { id: REENGAGE_7DAY_ID },
        { id: REENGAGE_14DAY_ID },
      ],
    });
  } catch (err) {
    console.warn('[알림] E. 이탈 방지 취소 실패:', err);
  }
}

// ===================================================================
// F. 고민 상담 후속 알림 (decision 모드 저장 3일 후)
// ===================================================================

/**
 * 고민 상담(decision) 기록 저장 후 3일 뒤 20시에 후속 알림을 예약합니다.
 * "그 고민이 지금 어떻게 됐는지" 기록을 유도합니다.
 */
export async function scheduleDecisionFollowUpPush() {
  if (!Capacitor.isNativePlatform()) return;
  try {
    await cancelDecisionFollowUpPush();
    const at = new Date();
    at.setDate(at.getDate() + 3);
    at.setHours(20, 0, 0, 0);
    await LocalNotifications.schedule({
      notifications: [{
        ...NOTIF_DEFAULTS,
        id: DECISION_FOLLOWUP_ID,
        title: '그 고민, 지금은 어떻게 됐어요?',
        body: '결정하기 어려웠던 일이 지금 어떻게 됐는지 기록해볼까요?',
        extra: { type: 'decision-followup' },
        schedule: { at, allowWhileIdle: true },
      }],
    });
    console.log('[알림] F. 고민 후속 알림 3일 후 예약');
  } catch (err) {
    console.warn('[알림] F. 고민 후속 예약 실패:', err);
  }
}

/** 고민 상담 후속 알림 취소 */
export async function cancelDecisionFollowUpPush() {
  if (!Capacitor.isNativePlatform()) return;
  try {
    await LocalNotifications.cancel({ notifications: [{ id: DECISION_FOLLOWUP_ID }] });
  } catch (err) {
    console.warn('[알림] F. 고민 후속 취소 실패:', err);
  }
}

// ===================================================================
// G. 꿈과 목표 첫 행동 팔로우업 (dream 세션 저장 후 7일)
// ===================================================================

/**
 * 꿈과 목표(dream) 기록 저장 후 7일 뒤 20시에 후속 알림을 예약합니다.
 * "이번 주 첫 한 걸음, 해봤어?"로 firstStep 실행 여부를 되물어줍니다.
 */
export async function scheduleDreamFirstStepFollowUpPush() {
  if (!Capacitor.isNativePlatform()) return;
  try {
    await cancelDreamFirstStepFollowUpPush();
    const at = new Date();
    at.setDate(at.getDate() + 7);
    at.setHours(20, 0, 0, 0);
    await LocalNotifications.schedule({
      notifications: [{
        ...NOTIF_DEFAULTS,
        id: DREAM_FIRSTSTEP_ID,
        title: '이번 주 첫 한 걸음, 해봤어?',
        body: '꿈과 목표에서 정했던 첫 행동을 기록해볼까요?',
        extra: { type: 'dream-firststep' },
        schedule: { at, allowWhileIdle: true },
      }],
    });
    console.log('[알림] G. 꿈 첫 걸음 팔로우업 7일 후 예약');
  } catch (err) {
    console.warn('[알림] G. 꿈 첫 걸음 팔로우업 예약 실패:', err);
  }
}

/** 꿈과 목표 첫 행동 팔로우업 알림 취소 */
export async function cancelDreamFirstStepFollowUpPush() {
  if (!Capacitor.isNativePlatform()) return;
  try {
    await LocalNotifications.cancel({ notifications: [{ id: DREAM_FIRSTSTEP_ID }] });
  } catch (err) {
    console.warn('[알림] G. 꿈 첫 걸음 취소 실패:', err);
  }
}

// ===================================================================
// H. 첫 기록 기념 (앱 설치 후 첫 세션 저장 직후)
// ===================================================================

/**
 * 앱 설치 후 처음으로 기록을 저장했을 때 2초 후 즉시 축하 알림을 보냅니다.
 * 스트릭 축하(D-1)가 3일부터 시작하므로 첫날 긍정 피드백을 제공합니다.
 */
export async function sendFirstRecordCelebration() {
  if (!Capacitor.isNativePlatform()) return;
  try {
    await LocalNotifications.schedule({
      notifications: [{
        ...NOTIF_DEFAULTS,
        id: FIRST_RECORD_ID,
        title: '첫 기록을 남겼어요!',
        body: '이제부터 옴살이 기억해줄게요.',
        extra: { type: 'first-record' },
        schedule: { at: new Date(Date.now() + 2000) },
      }],
    });
    console.log('[알림] H. 첫 기록 기념 알림 발송');
  } catch (err) {
    console.warn('[알림] H. 첫 기록 기념 실패:', err);
  }
}

// ===================================================================
// I. 나와의 대화 미확인 알림 (AI 메시지 생성 후 5분 뒤)
// ===================================================================

/**
 * AI가 나와의 대화에 새 메시지를 남겼을 때 5분 뒤 알림을 예약합니다.
 * 유저가 5분 안에 채팅 탭을 열면 cancelNewChatMessagePush()로 취소합니다.
 */
export async function scheduleNewChatMessagePush() {
  if (!Capacitor.isNativePlatform()) return;
  try {
    await cancelNewChatMessagePush();
    const at = new Date(Date.now() + 5 * 60 * 1000); // 5분 후
    await LocalNotifications.schedule({
      notifications: [{
        ...NOTIF_DEFAULTS,
        id: NEW_CHAT_MSG_ID,
        title: 'AI가 새 메시지를 남겼어요',
        body: '나와의 대화에서 확인해보세요.',
        extra: { type: 'unread-chat' },
        schedule: { at, allowWhileIdle: true },
      }],
    });
    console.log('[알림] I. 채팅 미확인 알림 5분 후 예약');
  } catch (err) {
    console.warn('[알림] I. 채팅 미확인 알림 예약 실패:', err);
  }
}

/** 나와의 대화 미확인 알림 취소 (유저가 채팅 탭을 열었을 때 호출) */
export async function cancelNewChatMessagePush() {
  if (!Capacitor.isNativePlatform()) return;
  try {
    await LocalNotifications.cancel({ notifications: [{ id: NEW_CHAT_MSG_ID }] });
  } catch (err) {
    console.warn('[알림] I. 채팅 미확인 알림 취소 실패:', err);
  }
}
